import { z } from "zod/v4";
import type { clubSkillLevelSchema } from "../skill-level";

// Per-dimension coaching assessment (concept C). Club-staff-private —
// never surfaced to the assessed member, another club, or a public profile.
// Distinct from the global skill tier (concept A, `users.skill_level`) and
// the club level (concept B, `club_skill_levels`); C only ever *suggests* a
// club level, it never writes A or B directly.

export const dimensionSchema = z.enum(["attack", "block", "serve", "receive", "set", "defence"]);

const RATING_MIN = 1;
const RATING_MAX = 5;

export const ratingSchema = z.number().int().min(RATING_MIN).max(RATING_MAX);

// The assessment carries all six dimensions or none — a plain zod object
// with every key required rejects a partial set outright.
export const ratingsSchema = z.object({
	attack: ratingSchema,
	block: ratingSchema,
	defence: ratingSchema,
	receive: ratingSchema,
	serve: ratingSchema,
	set: ratingSchema,
});

export const assessmentSchema = z.object({
	assessed_at: z.coerce.date(),
	assessed_by: z.string().uuid(),
	note: z.string().nullable().optional(),
	overall: z.number().min(RATING_MIN).max(RATING_MAX),
	ratings: ratingsSchema,
	user_id: z.string().uuid(),
});

export const assessmentListSchema = z.object({
	items: z.array(assessmentSchema),
});

export const upsertAssessmentSchema = z.object({
	note: z.string().nullable().optional(),
	ratings: ratingsSchema,
});

// ─── Club-level rollup ──────────────────────────────────────────────────────
//
// `overall` (the mean of the six ratings, always in [1, 5]) buckets onto one
// of the club's `club_skill_levels` rows. The service that owns assessments
// does not know about club levels at all — this is purely a frontend mapping
// from a score to a suggestion, which the trainer applies or ignores.
//
// The club's levels are an ordered, variable-length list (`sort_order`), so
// this divides the [1, 5] range into `levels.length` equal-width buckets,
// half-open except the last, which is closed at 5.

const RATING_RANGE = RATING_MAX - RATING_MIN;

export type SkillLevelCandidate = z.infer<typeof clubSkillLevelSchema>;

export type SkillLevelSuggestion =
	| { level: SkillLevelCandidate; type: "suggested" }
	| { type: "no_levels" };

export function suggestSkillLevel(
	overall: number,
	levels: readonly SkillLevelCandidate[],
): SkillLevelSuggestion {
	if (levels.length === 0) {
		return { type: "no_levels" };
	}

	const ordered = [...levels].sort((a, b) => a.sort_order - b.sort_order);
	const bucketWidth = RATING_RANGE / ordered.length;
	const rawIndex = Math.floor((overall - RATING_MIN) / bucketWidth);
	const index = Math.min(Math.max(rawIndex, 0), ordered.length - 1);

	return { level: ordered[index], type: "suggested" };
}
